import { useEffect, useMemo } from "react"
import Link from "next/link"
import styles from "./GiftingForm.module.scss"
import TieupImg from "../../common/TieupImg"
import { useForm } from "react-hook-form"
import { GiftItem } from "src/models/gift/GiftItem"
import { SENT_FIXED_MESSAGE_DEFINITIONS } from "src/models/gift/fixedMessage/SENT_FIXED_MESSAGES_DEFINITIONS"
import routes from "routes"

export type FormParams = {
  gift_item_id: string
  amount: number
  send_message_id: string
  send_free_message: string
}

type Props = {
  giftItems: GiftItem[]
  onSubmit: (data: FormParams) => void
  returnURL: string
  isHiddenSubmitButton: boolean
}

const GiftingForm: React.FC<Props> = ({
  giftItems,
  onSubmit,
  returnURL,
  isHiddenSubmitButton,
}) => {
  const { watch, handleSubmit, register, setValue, errors } =
    useForm<FormParams>({
      defaultValues: {
        gift_item_id: undefined,
        amount: 0,
        send_message_id: undefined,
        send_free_message: "",
      },
    })

  const watchGiftItemId = watch("gift_item_id")

  useEffect(() => {
    if (watchGiftItemId) {
      setValue("amount", 0)
    }
  }, [setValue, watchGiftItemId])

  const maxAmount = useMemo(() => {
    const selectedGift = giftItems.find(
      (v) => Number(watchGiftItemId) === v.type.id,
    )

    return selectedGift ? selectedGift.amount : 0
  }, [watchGiftItemId, giftItems])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.section}>
        <h3 className={styles.section_header}>
          ギフト選択<span className={styles.section_annotation}>※必須</span>
        </h3>
        <Link href={routes.mypageGiftPurchase(returnURL)}>
          <a className={styles.section_link}>ギフト購入</a>
        </Link>
        <div className={`${styles.section_body} ${styles.border}`}>
          <ul className={styles.itemList}>
            {giftItems.map((v) => (
              <li className={styles.giftItem} key={v.type.id}>
                <input
                  className={styles.giftItem_input}
                  type="radio"
                  name="gift_item_id"
                  id={`giftItemInput${v.type.id}`}
                  value={v.type.id}
                  ref={register({ required: true })}
                  disabled={v.amount === 0}
                />
                <label
                  className={styles.giftItem_label}
                  htmlFor={`giftItemInput${v.type.id}`}
                >
                  <span className={styles.giftItem_img}>
                    <TieupImg src={v.type.iconUrl} />
                  </span>
                  <span className={styles.giftItem_text}>
                    <span className={styles.giftItem_name}>{v.type.name}</span>
                    <span className={styles.giftItem_amount}>
                      所持: {v.amount}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <div className={styles.giftAmount}>
            <label className={styles.giftAmount_label}>
              選択したギフトの送付数
            </label>
            <input
              disabled={!watchGiftItemId}
              className={styles.giftAmount_input}
              name="amount"
              type="number"
              min="0"
              max={maxAmount}
              ref={register({ required: true, min: 1, max: maxAmount })}
            />
          </div>
          {(errors.gift_item_id || errors.amount) && (
            <p className={styles.errorMessage}>
              ギフトを選択し、所持数以下で送付数を入力してください。
            </p>
          )}
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.section_header}>
          応援メッセージ選択
          <span className={styles.section_annotation}>※必須</span>
        </h3>
        <div className={styles.section_body}>
          <ul className={styles.itemList}>
            {SENT_FIXED_MESSAGE_DEFINITIONS.map((v) => (
              <li className={styles.replyItem} key={v.id}>
                <input
                  className={styles.replyItem_input}
                  type="radio"
                  name="send_message_id"
                  id={`replyItemInput${v.id}`}
                  value={v.id}
                  ref={register({ required: true })}
                />
                <label
                  className={styles.replyItem_label}
                  htmlFor={`replyItemInput${v.id}`}
                >
                  {v.value}
                </label>
              </li>
            ))}
          </ul>
          {errors.send_message_id && (
            <p className={styles.errorMessage}>
              応援メッセージを選択してください。
            </p>
          )}
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.section_header}>
          フリーメッセージ
          <span className={styles.section_annotation}>※任意</span>
        </h3>
        <div className={styles.section_body}>
          <textarea
            name="send_free_message"
            ref={register()}
            className={styles.messageText}
          ></textarea>
        </div>
      </div>

      <div className={styles.submit}>
        <p className={styles.submit_text}>
          贈ったギフトとメッセージは取り消すことができません。宛先と内容をご確認ください。
        </p>
        {!isHiddenSubmitButton && (
          <button className={styles.submit_btn}>贈る</button>
        )}
      </div>
    </form>
  )
}

export default GiftingForm
